import React, { useState } from 'react';
import { Blaetterleiste, TabellenKopf, useTabelle, type SpaltenDefinition } from '../components/TabellenSteuerung';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, XCircle } from 'lucide-react';
import { deployments } from '../api';
import StatusBadge from '../components/StatusBadge';
import type { JobStatus, JobType } from '../types';
import { formatDistanceToNow, format } from 'date-fns';
import { useEinstellung } from '../contexts/EinstellungenContext';

const JOB_STATUSES: JobStatus[] = ['PENDING', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELLED', 'RETRYING'];
const JOB_TYPES: JobType[] = [
  'CREATE_SERVER', 'PREPARE_SERVER', 'CREATE_INSTANCE', 'UPDATE_INSTANCE',
  'REDEPLOY_INSTANCE', 'STOP_INSTANCE', 'START_INSTANCE', 'DELETE_INSTANCE',
  'BACKUP_INSTANCE', 'EXPORT_INSTANCE', 'COLLECT_METRICS', 'SYNC_WOOCOMMERCE',
  'CLEANUP_ABANDONED_ORDERS',
];

export default function DeploymentsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useEinstellung<string>('deployments.status', '');
  const [typeFilter, setTypeFilter] = useEinstellung<string>('deployments.typ', '');
  const [logJob, setLogJob] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['deployments', { status: statusFilter, type: typeFilter }],
    queryFn: () =>
      deployments.list({
        status: statusFilter || undefined,
        type: typeFilter || undefined,
      }),
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => deployments.retry(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deployments'] }),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => deployments.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deployments'] }),
  });

  const rows = data ?? [];

  const spalten: SpaltenDefinition<(typeof rows)[number]>[] = [
    { key: 'typ', label: t('tables.type'), wert: (j) => j.type },
    { key: 'status', label: t('common.status'), wert: (j) => j.status },
    { key: 'instanz', label: t('tables.instance'), wert: (j) => j.instance?.domain ?? '' },
    { key: 'server', label: t('tables.server'), wert: (j) => j.server?.hostname ?? '' },
    { key: 'erstellt', label: t('common.createdAt'), wert: (j) => j.createdAt },
    { key: 'dauer', label: t('tables.duration'), wert: (j) => (j.startedAt && j.finishedAt ? new Date(j.finishedAt).getTime() - new Date(j.startedAt).getTime() : null) },
    { key: 'versuche', label: t('tables.retries'), wert: (j) => j.retryCount ?? 0 },
  ];

  const tabelle = useTabelle({
    speichern: 'deployments',
    zeilen: rows,
    spalten,
    startSortierung: { key: 'erstellt', richtung: 'ab' },
  });


  const logJobData = logJob ? rows.find((r) => r.id === logJob) : null;

  function duration(job: (typeof rows)[0]) {
    if (!job.startedAt) return '—';
    const end = job.finishedAt ? new Date(job.finishedAt) : new Date();
    const secs = Math.round((end.getTime() - new Date(job.startedAt).getTime()) / 1000);
    if (secs < 60) return `${secs}s`;
    return `${Math.round(secs / 60)}m ${secs % 60}s`;
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">{t('filters.allStatuses')}</option>
          {JOB_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">{t('filters.allTypes')}</option>
          {JOB_TYPES.map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <span className="ml-auto text-sm text-slate-500">{rows.length} jobs</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-surface">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <TabellenKopf spalten={spalten} steuerung={tabelle} extra={[t('common.actions')]} />
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 8 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-200 rounded" /></td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-slate-400 italic">{t('empty.deployments')}</td>
              </tr>
            ) : (
              tabelle.sichtbar.map((job) => (
                <tr key={job.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">
                    {job.type.replace(/_/g, ' ')}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={job.status} type="job" />
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs font-mono">
                    {job.instance?.domain ?? job.instanceId?.slice(0, 10) ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {job.server?.hostname ?? job.serverId?.slice(0, 10) ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{duration(job)}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs text-center">
                    {job.retryCount}/{job.maxRetries}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setLogJob(logJob === job.id ? null : job.id)}
                        className="text-xs text-brand-600 hover:text-brand-800"
                      >
                        Logs
                      </button>
                      {(job.status === 'FAILED' || job.status === 'CANCELLED') && (
                        <button
                          onClick={() => retryMutation.mutate(job.id)}
                          disabled={retryMutation.isPending}
                          className="inline-flex items-center gap-0.5 text-xs text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
                        >
                          <RotateCcw size={11} /> Retry
                        </button>
                      )}
                      {(job.status === 'PENDING' || job.status === 'RUNNING') && (
                        <button
                          onClick={() => cancelMutation.mutate(job.id)}
                          disabled={cancelMutation.isPending}
                          className="inline-flex items-center gap-0.5 text-xs text-red-500 hover:text-red-700 disabled:opacity-50"
                        >
                          <XCircle size={11} /> Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Blaetterleiste blaettern={tabelle.blaettern} />
      {/* Log modal */}
      {logJob && logJobData && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setLogJob(null)}
        >
          <div
            className="bg-konsole rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
              <div>
                <span className="text-slate-300 font-mono text-sm">{logJobData.type.replace(/_/g, ' ')}</span>
                <span className="ml-3">
                  <StatusBadge status={logJobData.status} type="job" />
                </span>
              </div>
              <button onClick={() => setLogJob(null)} className="text-slate-400 hover:text-white text-xl leading-none">×</button>
            </div>
            {logJobData.errorMessage && (
              <div className="px-5 py-3 bg-red-900/30 border-b border-red-700/30 text-red-300 text-xs">
                {logJobData.errorMessage}
              </div>
            )}
            <div className="flex items-center gap-4 px-5 py-2 border-b border-slate-700 text-xs text-slate-400">
              {logJobData.startedAt && <span>Started: {format(new Date(logJobData.startedAt), 'PPp')}</span>}
              {logJobData.finishedAt && <span>Finished: {format(new Date(logJobData.finishedAt), 'PPp')}</span>}
            </div>
            <pre className="flex-1 overflow-y-auto p-5 text-xs text-green-300 font-mono whitespace-pre-wrap break-all scrollbar-thin">
              {logJobData.logs ?? 'No logs available for this job.'}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
